import {defineMessage} from 'react-intl';
import * as React from 'react';
import {setup, run} from '../../snippets/java';
import comment from '../../snippets/comment';
import {locales} from '../sampleLocations';
import {CreateShellCommand} from '../../../components/left-col';

const createIntent = (country, reader) => {
  if (reader === 'wpe') {
    const currency = locales[country].currency;
    const pmTypes =
      country === 'CA'
        ? `.addAllPaymentMethodType(Arrays.asList("card_present", "interac_present"))`
        : `.addPaymentMethodType("card_present")`;
    return `
{+create-intent}
    post("/create_payment_intent", (request, response) -> {
      response.type("application/json");

      PaymentIntentParams postBody = gson.fromJson(request.body(), PaymentIntentParams.class);

      // For Terminal payments, the 'payment_method_types' parameter must include
      // 'card_present' and the 'capture_method' must be set to 'manual'
      PaymentIntentCreateParams createParams = PaymentIntentCreateParams.builder()
        .setCurrency("${currency}")
        .setAmount(postBody.getAmount())
        .setCaptureMethod(PaymentIntentCreateParams.CaptureMethod.MANUAL)
        ${pmTypes}
        .build();
      // Create a PaymentIntent with the order amount and currency
      PaymentIntent {span response create-intent}intent{/span}= PaymentIntent.create(createParams);

      Map<String, String> map = new HashMap();
      map.put("client_secret", intent.getClientSecret());

      return gson.toJson(map);
    });
    {/create-intent}
    `;
  }
};

const createLocation = (country) => {
  const location = locales[country].location;
  const city = location.address.city
    ? `
      .setCity("${location.address.city}")`
    : '';
  const state = location.address.state
    ? `
      .setState("${location.address.state}")`
    : '';
  return `
  public static Location createLocation() throws StripeException{
    {+create-location}
    LocationCreateParams.Address address =
    LocationCreateParams.Address.builder()
      .setLine1("${location.address.line1}")${city}${state}
      .setCountry("${location.address.country}")
      .setPostalCode("${location.address.postalCode}")
      .build();

    LocationCreateParams params =
      LocationCreateParams.builder()
        .setDisplayName("${location.displayName}")
        .setAddress(address)
        .build();

    Location location = Location.create(params);
    {/create-location}

    return location;
  }
  `;
};

export const steps = {
  setup,
  'create-location': {},
  'connection-token-endpoint': {},
  'create-intent': {},
  'capture-intent-endpoint': {},
  'run-server': {
    body: {
      message: defineMessage({
        id: 'docsApp.ib.terminal.server.java.run-server.body',
        defaultMessage: 'Run your server and go to <a>localhost:4242</a>.',
      }),
      values: {
        a: (str) => (
          <a href="http://localhost:4242" target="_blank">
            {str}
          </a>
        ),
      },
    },
    Component: CreateShellCommand(run('accept-card-payment')),
  },
};

export default ({
  keys,
  merchant,
  reader,
  country,
}) => `package com.stripe.sample;

import java.nio.file.Paths;

import static spark.Spark.get;
import static spark.Spark.post;
import static spark.Spark.staticFiles;
import static spark.Spark.port;

import com.google.gson.Gson;
import com.google.gson.annotations.SerializedName;

import com.stripe.Stripe;
import com.stripe.model.PaymentIntent;
import com.stripe.param.PaymentIntentCreateParams;
import com.stripe.model.terminal.ConnectionToken;
import com.stripe.param.terminal.ConnectionTokenCreateParams;
import com.stripe.model.terminal.Location;
import com.stripe.param.terminal.LocationCreateParams;
import com.stripe.exception.StripeException;

import java.util.Map;
import java.util.HashMap;

public class Server {
  private static Gson gson = new Gson();

  static class PaymentIntentParams {
    private String id;
    private long amount;

    public String getID() {
      return id;
    }

    public long getAmount() {
      return amount;
    }
  }

  public static void main(String[] args) {
    port(4242);
    staticFiles.externalLocation(Paths.get("public").toAbsolutePath().toString());

    {+setup}
    ${comment && comment(merchant)}
    Stripe.apiKey = "${keys.secret}";
    {/setup}

    // The ConnectionToken's secret lets you connect to any Stripe Terminal reader
    // and take payments with your Stripe account.
    // Be sure to authenticate the endpoint for creating connection tokens.
    {+connection-token-endpoint}
    post("/connection_token", (request, response) -> {
      response.type("application/json");
      ConnectionTokenCreateParams params = ConnectionTokenCreateParams.builder()
        .build();

      ConnectionToken {span response connection-token-endpoint}connectionToken{/span} = ConnectionToken.create(params);

      Map<String, String> map = new HashMap();
      map.put("secret", connectionToken.getSecret());

      return gson.toJson(map);
    });
    {/connection-token-endpoint}
    ${createIntent(country, reader)}
    {+capture-intent-endpoint}
    post("/capture_payment_intent", (request, response) -> {
      response.type("application/json");

      PaymentIntentParams postBody = gson.fromJson(request.body(), PaymentIntentParams.class);

      PaymentIntent intent = PaymentIntent.retrieve(postBody.getID());
      {span response capture-intent-endpoint}intent{/span} = intent.capture();

      return intent.toJson();
    });
    {/capture-intent-endpoint}
  }

  ${createLocation(country)}
}`;
